import { getBaseWithPostfix } from '@gef-ui/redux-common/selectors/urls';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { batch, useDispatch, useSelector } from 'react-redux';
import { getData, getRestUrl } from '../../facade';
import { createEmitAction } from '../../middleware/reactiveApi/stream/actions';
import { clearCachedRequest } from '../../redux/cache/actions';
import { getRequestData } from '../../redux/cache/selectors';
import { clearRequestStatus } from '../../redux/status/actions';
import { getRequestStatusById, makeGetRequestState } from '../../redux/status/selectors';
const createApiAction = (endpoint, cancel$, meta, resolvedCacheKey)=>getData({
        endpoint,
        cancel: cancel$,
        meta: {
            ...meta || {},
            requestUuid: resolvedCacheKey
        },
        cache: {
            cacheKey: resolvedCacheKey
        }
    });
const resolveCacheKey = (cacheKey, generatedUrl, endpoint, pathVariables, urlParams, routeParams)=>{
    if (typeof cacheKey === 'function') {
        return cacheKey({
            generatedUrl,
            endpoint,
            pathVariables,
            urlParams,
            routeParams
        });
    } else if (cacheKey) {
        return cacheKey;
    }
    return generatedUrl;
};
/**
 * Encapsulate logic around GET and caching
 *
 * @documented
 * @category Field
 * @import import useCachedQuery from '@gef-ui/middleware-api/hooks/useCachedQuery'
 * @param {Object} input
 * @param {String} input.endpoint - endpoint to call
 * @param {String} [input.cacheKey] - optional custom cache key
 * @param {function} [input.baseUrlSelector=getBaseWithPostfix] - base url selector
 * @param {Object} [input.pathVariables]
 * @param {Object} [input.urlParams]
 * @param {Object} [input.routeParams]
 * @param {boolean} [input.keepPreviousData] - when endpoint or params changes, keep previous data in cache
 * @param {boolean} [input.enabled] - query enabled
 *
 * @example
 *
 * 	const codelist = useCachedQuery({endpoint: 'codelist'});
 *
 */ const useCachedQuery = ({ endpoint, cacheKey, baseUrlSelector = getBaseWithPostfix, pathVariables, meta, urlParams, routeParams, keepPreviousData, enabled = true })=>{
    const [isEnabled, setIsEnabled] = useState(enabled);
    const codelistCalled = useRef(false);
    const unmountPhase = useRef(false);
    const getRequestState = useMemo(makeGetRequestState, []);
    const dispatch = useDispatch();
    const generatedUrl = getRestUrl(endpoint, {
        pathVariables,
        urlParams,
        routeParams
    });
    const apiEndpoint = useMemo(()=>baseUrlSelector(generatedUrl), [
        baseUrlSelector,
        generatedUrl
    ]);
    const resolvedCacheKey = resolveCacheKey(cacheKey, generatedUrl, endpoint, pathVariables, urlParams, routeParams);
    const cachedRequest = useSelector(getRequestData(resolvedCacheKey));
    const hasCacheRecord = !!cachedRequest;
    const requestStatus = useSelector((state)=>getRequestStatusById(state)(resolvedCacheKey));
    const requestState = useSelector((state)=>getRequestState(state, resolvedCacheKey));
    const { action$: cancel$, emitAction: emitCancel } = useMemo(createEmitAction, []);
    if (isEnabled !== enabled) {
        // note: this behavior may not be clear, but it is intended
        // https://react.dev/learn/you-might-not-need-an-effect#adjusting-some-state-when-a-prop-changes
        setIsEnabled(enabled);
    }
    const remove = useCallback(()=>{
        batch(()=>{
            dispatch(clearCachedRequest(resolvedCacheKey));
            dispatch(clearRequestStatus(resolvedCacheKey));
        });
    }, [
        dispatch,
        resolvedCacheKey
    ]);
    const refetch = useCallback(()=>{
        dispatch(createApiAction(apiEndpoint, cancel$, meta, resolvedCacheKey));
    }, [
        apiEndpoint,
        cancel$,
        dispatch,
        meta,
        resolvedCacheKey
    ]);
    useEffect(()=>()=>{
            unmountPhase.current = true;
        }, []);
    useEffect(()=>{
        codelistCalled.current = false;
        return ()=>{
            // v clean je to proto ze to pouziva tu starou instanci co chceme provolat
            if (!unmountPhase.current && !keepPreviousData) {
                remove();
            }
            emitCancel();
        };
    }, [
        keepPreviousData,
        remove,
        emitCancel
    ]);
    useEffect(()=>{
        if (isEnabled && !hasCacheRecord && !requestState.isLoading && !codelistCalled.current) {
            dispatch(createApiAction(apiEndpoint, cancel$, meta, resolvedCacheKey));
            codelistCalled.current = true;
        }
    }, [
        apiEndpoint,
        hasCacheRecord,
        requestState.isLoading,
        resolvedCacheKey,
        dispatch,
        isEnabled,
        cancel$,
        meta
    ]);
    return {
        data: cachedRequest?.body ? {
            data: cachedRequest?.body,
            headers: cachedRequest?.headers,
            status: cachedRequest?.status
        } : undefined,
        requestStatus,
        ...requestState,
        isLoading: !enabled ? false : !hasCacheRecord || requestState.isLoading,
        isIdle: !enabled,
        remove,
        enable: refetch,
        // keep api same as in react-query
        refetch
    };
};
export default useCachedQuery;
